import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth/auth.service';
import { EdeskStateService } from './edesk-state.service';

// ✅ use generated types
import { Folder } from '../api/model/folder';
import { GetFoldersResponse } from '../api/model/getFoldersResponse';
import { GetMessageHeadersResponse } from '../api/model/getMessageHeadersResponse';
import { GetMessageDetailResponse } from '../api/model/getMessageDetailResponse';
import { FindIdentityResponse } from '../api';
import { AuthorizeMessageResponse } from '../api/model/authorizeMessageResponse';
// Draft workflow types are generated from the separate draft API spec (src/app/api-draft)
import { SendGeneralAgendaRequest } from '../api-draft';
import { CreateDraftRequest } from '../api-draft/model/createDraftRequest';
import { CreateDraftResponse } from '../api-draft/model/createDraftResponse';
import { AttachmentDto } from '../api-draft/model/attachmentDto';
import { SignAndSendRequest } from '../api-draft/model/signAndSendRequest';
import { SignAndSendResponse } from '../api-draft/model/signAndSendResponse';
import { RemoveSignatureRequest } from '../api-draft/model/removeSignatureRequest';
import { DraftListResponse } from '../api/model/draftListResponse';
import { DraftSummaryDto } from '../api/model/draftSummaryDto';
import { LoadDraftResponse } from '../api/model/loadDraftResponse';
import { AttachmentFullDto } from '../api/model/attachmentFullDto';
import { RecipientDto } from '../api/model/recipientDto';
import { GetEDeskDelegationsResponse } from '../api/model/getEDeskDelegationsResponse';
import { MessageHeader } from '../api/model/messageHeader';

// Re-export for convenience
export type { CreateDraftRequest, CreateDraftResponse, AttachmentDto, SignAndSendRequest, SignAndSendResponse, RemoveSignatureRequest, DraftListResponse, DraftSummaryDto, LoadDraftResponse, AttachmentFullDto, RecipientDto };

/**
 * MessageHeader rozšírený o polia, ktoré backend vracia nad rámec OpenAPI schémy
 * (SenderId/RecipientId nie sú deklarované v MessageHeader schéme, ale backend ich posiela).
 */
export interface MessageHeaderExt extends MessageHeader {
  SenderId?: string | null;
  RecipientId?: string | null;
}

@Injectable({ providedIn: 'root' })
export class EdeskService {
  private get base() { return environment.apiBase; }
  private get downloaderApiBase() { return environment.downloaderApiBase; }

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private edeskStateService: EdeskStateService
  ) {}

  /** Build headers with x-edesk-uri from the selected eDesk state */
  private edeskHeaders(extra?: Record<string, string>): HttpHeaders {
    let headers = new HttpHeaders(extra);
    const edeskUri = this.edeskStateService.selectedEdeskUri();
    if (edeskUri) {
      headers = headers.set('x-edesk-uri', edeskUri);
    }
    return headers;
  }

  /** Get list of eDesk delegations for the current user (GET /edesk/delegation) */
  getDelegations(): Observable<GetEDeskDelegationsResponse> {
    return this.http.get<GetEDeskDelegationsResponse>(`${this.base}/edesk/delegation`);
  }

  getFolders(): Observable<GetFoldersResponse> {
    return this.http.get<GetFoldersResponse>(`${this.base}/folder`, { headers: this.edeskHeaders() });
  }

  getMessageHeaders(
    folderId: number,
    count = 10,
    skip = 0
  ): Observable<GetMessageHeadersResponse> {
    const params = new HttpParams()
      .set('count', count)
      .set('skip', skip);
    return this.http.get<GetMessageHeadersResponse>(
      `${this.base}/folder/${folderId}/message-header`,
      { params, headers: this.edeskHeaders() }
    );
  }

  getMessageDetail(messageId: number) {
    return this.http.get<GetMessageDetailResponse>(
      `${this.base}/message/${messageId}`,
      { headers: this.edeskHeaders() }
    );
  }

  /** Binary download for one message part */
  downloadMessagePart(
    messageId: number,
    partId: string
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.base}/message/${messageId}/part/${encodeURIComponent(partId)}`,
      {
        headers: this.edeskHeaders(),
        responseType: 'blob',
        observe: 'response',
      }
    );
  }

  findIdentity(identity: string): Observable<FindIdentityResponse> {
    const params = new HttpParams().set('uri', identity);
    return this.http.get<FindIdentityResponse>(`${this.base}/identity`, { params });
  }

  /** Send a general agenda message (POST /sendGeneralAgenda) */
  sendGeneralAgenda(payload: SendGeneralAgendaRequest): Observable<any> {
    return this.http.post(`${this.base}/sendGeneralAgenda`, payload, { headers: this.edeskHeaders() });
  }

  /**
   * Authorize a message (POST /message/{id}/authorize).
   * The backend requires a mandatory `x-audit-log` header carrying a JSON-serialized
   * ClientReceiptConfirmation object (task #63, see ClientReceiptConfirmation in the
   * OpenAPI spec): authorizedAt (UTC), mechanism and clientContext are all mandatory.
   * Its content is written to the server-side audit log.
   */
  authorizeMessage(messageId: number, clientReceiptConfirmation?: Record<string, unknown>): Observable<AuthorizeMessageResponse> {
    const confirmation = clientReceiptConfirmation ?? {
      authorizedAt: new Date().toISOString(),
      mechanism: 'CONFIRMATION_DIALOG',
      clientContext: {
        channel: 'BROWSER',
        appIdentifier: 'qsign-angular-edesk-demo',
        appVersion: '1.0.0',
        platform: navigator.userAgent,
      },
    };
    return this.http.post<AuthorizeMessageResponse>(
      `${this.base}/message/${messageId}/authorize`,
      null,
      { headers: this.edeskHeaders({ 'x-audit-log': JSON.stringify(confirmation) }) }
    );
  }

  /** Save or update a draft (POST /draft/save) */
  saveDraft(payload: CreateDraftRequest): Observable<CreateDraftResponse> {
    return this.http.post<CreateDraftResponse>(`${this.base}/draft/save`, payload, { headers: this.edeskHeaders() });
  }

  /** Sign and send a draft (POST /draft/sign) */
  signDraft(payload: SignAndSendRequest): Observable<SignAndSendResponse> {
    return this.http.post<SignAndSendResponse>(`${this.base}/draft/sign`, payload, { headers: this.edeskHeaders() });
  }

  /** Get list of drafts (GET /draft/list) */
  getDraftList(count: number, skip: number): Observable<DraftListResponse> {
    return this.http.get<DraftListResponse>(`${this.base}/draft/list`, {
      params: { count: count.toString(), skip: skip.toString() }
    });
  }

  /** Load a specific draft for editing (GET /draft/{draftId}/load) */
  loadDraft(draftId: number): Observable<LoadDraftResponse> {
    return this.http.get<LoadDraftResponse>(`${this.base}/draft/${draftId}/load`);
  }

  /** Delete a draft (DELETE /draft/{draftId}) */
  deleteDraft(draftId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/draft/${draftId}`);
  }

  /**
   * Remove signatures from a draft (POST /draft/{draftId}/removeSignature).
   * @param documentNames Names of the documents to unsign (the form is addressed
   *                      as GeneralAgenda.xml). Omitted/empty = remove all signatures.
   */
  removeSignature(draftId: number, documentNames?: string[]): Observable<void> {
    const body: RemoveSignatureRequest | null =
      documentNames && documentNames.length > 0 ? { documentNames } : null;
    return this.http.post<void>(`${this.base}/draft/${draftId}/removeSignature`, body, { headers: this.edeskHeaders() });
  }

  /** Get list of available recipients (GET /recipient/list) */
  getRecipients(query?: string, limit?: number): Observable<RecipientDto[]> {
    let params = new HttpParams();
    if (query) {
      params = params.set('query', query);
    }
    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }
    return this.http.get<RecipientDto[]>(`${this.base}/recipient/list`, { params });
  }

  /** Force refresh of recipient cache (POST /recipient/refresh) */
  refreshRecipients(): Observable<void> {
    return this.http.post<void>(`${this.base}/recipient/refresh`, {});
  }

  /** Get signing job status (GET /signature/status/{jobId}) */
  getSigningJobStatus(jobId: string): Observable<{ status: 'PENDING' | 'COMPLETED' | 'FAILED'; errorMessage?: string; message?: string }> {
    return this.http.get<{ status: 'PENDING' | 'COMPLETED' | 'FAILED'; errorMessage?: string; message?: string }>(
      `${this.base}/signature/status/${jobId}`
    );
  }
}

/** Helper to save the blob (tries to read filename from headers) */
export function saveBlobAs(resp: HttpResponse<Blob>, fallbackName = 'attachment.pdf') {
  const blob = resp.body!;
  let filename = fallbackName;

  const cd = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition');
  if (cd) {
    // Prefer RFC 5987 filename*=UTF-8''... form (URL-encoded, supports non-ASCII)
    const m5987 = /filename\*=UTF-8''([^;"\s]+)/i.exec(cd);
    if (m5987?.[1]) {
      filename = decodeURIComponent(m5987[1]);
    } else {
      // Fall back to plain filename= form
      const m = /filename="?([^";]+)"?/i.exec(cd);
      if (m?.[1]) filename = m[1];
    }
  }

  const isAndroidWebView = /Android/i.test(navigator.userAgent) && /wv|WebView/i.test(navigator.userAgent);

  if (isAndroidWebView) {
    // Android WebView cannot download blob: URLs — the download manager has no access to
    // in-memory blob objects and window.open() is typically blocked.
    // Strategy 1: delegate to a native Java bridge if the Android app exposes one.
    // Strategy 2: convert to a data: URL and trigger an anchor click (works for smaller files).
    const androidBridge = (window as any)['Android'] as { downloadFile?: (base64: string, name: string, mime: string) => void } | undefined;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string; // "data:<mime>;base64,<data>"
      if (androidBridge?.downloadFile) {
        // Let the native layer handle the download (no size limit, saves to Downloads folder).
        const base64 = dataUrl.split(',')[1] ?? '';
        androidBridge.downloadFile(base64, filename, blob.type || 'application/octet-stream');
      } else {
        // Fall back: use a data: URL with the anchor download attribute.
        // Works for reasonably-sized files; large files may hit memory limits.
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    };
    reader.readAsDataURL(blob);
  } else {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
}
